import { BeakerIcon } from "@heroicons/react/24/solid";
import { useVerification } from "src/contexts/VerificationContext";
import { useEffect, useState, useCallback, useMemo } from "react";
import BlurredCard from "../BlurredCard";
import { Medication } from "src/shared/types";
import Loading from "../Loading";
import { capitalizeStatus } from "src/utils/utils";
import { apiGet } from "src/utils/api";
import { StaticDataTable, StaticDataColumnProps, StaticDataRow } from "../table/StaticDataTable";
import { SortDirection } from "../table/useTableData";
import { useCurrentOrganization, useOrganizationContext } from "src/contexts/OrganizationContext";
import TableDateTime, { getDateTimeSortValue } from "../table/TableDateTime";
import TableBadge from "../table/TableBadge";
import PractitionerLink from "../table/PractitionerLink";

const statusColorMap: Record<string, string> = {
  active: "bg-emerald-500",
  stopped: "bg-rose-500",
  "on-hold": "bg-amber-500",
  completed: "bg-sky-500",
  "entered-in-error": "bg-rose-500",
};

const medicationColumns: StaticDataColumnProps[] = [
  { title: "Name", sortable: true },
  { title: "Date & Time", sortable: true, defaultSort: true, defaultSortDirection: SortDirection.DESC },
  { title: "Prescriber", sortable: true },
  { title: "Status", sortable: true },
  { title: "Dosage", sortable: false },
  { title: "Frequency", sortable: false },
  { title: "Refills", sortable: true },
  { title: "Reason", sortable: false },
];

const convertMedicationsToRows = (medications: Medication[]): StaticDataRow[][] => {
  return medications.map((medication) => [
    {
      name: "Name",
      value: medication.name,
      element: <span className="text-gray-900">{medication.name}</span>
    },
    {
      name: "Date & Time",
      value: getDateTimeSortValue(medication.date, medication.time),
      element: <TableDateTime date={medication.date} startTime={medication.time} />
    },
    {
      name: "Prescriber",
      value: medication.prescriber || "—",
      element: (
        <PractitionerLink
          name={medication.prescriber?.name || "—"}
          practitionerId={medication.prescriber?.practitioner_id}
        />
      )
    },
    {
      name: "Status",
      value: medication.status,
      element: (
        <TableBadge
          text={capitalizeStatus(medication.status)}
          bgColorClass={statusColorMap[medication.status] ?? "bg-gray-500"}
        />
      )
    },
    {
      name: "Dosage",
      value: medication.dosage ?? "—",
      element: <span className="text-gray-700">{medication.dosage ?? "—"}</span>
    },
    {
      name: "Frequency",
      value: medication.frequency ?? "—",
      element: <span className="text-gray-600 text-sm">{medication.frequency ?? "—"}</span>
    },
    {
      name: "Refills",
      value: typeof medication.refill === "number" ? medication.refill : -1,
      element: (
        <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-md text-xs font-medium tabular-nums ${
          medication.refill && medication.refill > 0 ? 'bg-brand-50 text-brand-700' : 'bg-gray-100 text-gray-500'
        }`}>
          {typeof medication.refill === "number" ? medication.refill : "-"}
        </span>
      )
    },
    {
      name: "Reason",
      value: medication.reasonCode ?? "—",
      element: <span className="text-gray-600 text-sm">{medication.reasonCode ?? "—"}</span>
    }
  ]);
};

const dummyMedications: Medication[] = [
  {
    name: "Medication 1",
    status: "active",
    dosage: "10mg",
    frequency: "Once daily",
    prescriber: {
      practitioner_id: "123",
      name: "Dr. John Doe",
      nameDetails: {
        use: "official",
        text: "Dr. John Doe",
      },
    },
    date: new Date().toISOString().split('T')[0],
    time: "10:00:00",
    refill: 10,
    reasonCode: "Hypertension",
  },
  {
    name: "Medication 2",
    status: "completed",
    dosage: "20mg",
    frequency: "Twice daily",
    prescriber: {
      practitioner_id: "456",
      name: "Dr. Jane Doe",
      nameDetails: {
        use: "official",
        text: "Dr. Jane Doe",
      },
    },
    date: new Date().toISOString().split('T')[0],
    time: "10:00:00",
    refill: 5,
    reasonCode: "Pain management",
  },
];

type MedicationCardProps = {
  yearFrom: string;
  yearTo: string;
  refreshKey?: number;
};

const MedicationCard = ({ yearFrom, yearTo, refreshKey }: MedicationCardProps) => {
  const organization = useCurrentOrganization();
  const { patientId } = useOrganizationContext();
  const [ medications, setMedications ] = useState<Medication[]>([]);
  const [ loading, setLoading ] = useState(false);

  const { isVerified } = useVerification();
  const showLoading = isVerified && loading;

  const fetchMedications = useCallback(async () => {
    if (!organization || !patientId) {
      setMedications([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const params = { yearFrom, yearTo };
      const response = await apiGet(`/api/${organization.id}/patients/${patientId}/medications`, params);
      const data = await response.json();

      if (data.error) {
        setMedications([]);
        window.location.href = "/error";
      }

      setMedications(data.medications);
    } catch (_) {
      setMedications([]);
    } finally {
      setLoading(false);
    }
  }, [ organization, patientId, yearFrom, yearTo ]);

  useEffect(() => {
    if (isVerified) {
      fetchMedications();
    } else {
      setMedications(dummyMedications);
    }
  }, [ isVerified, fetchMedications, yearFrom, yearTo, refreshKey ]);

  const tableRows = useMemo(() =>
    convertMedicationsToRows(medications),
    [ medications ]
  );

  return (
    <BlurredCard>
      {showLoading ? (
        <div className="flex justify-center mt-8">
          <Loading message="Loading Medications..." borderColor="border-brand-500" />
        </div>
      ) : (
        <div className="-mx-2">
          {medications.length > 0 ? (
            <StaticDataTable
              tableId="medications-table"
              columns={medicationColumns}
              rows={tableRows}
              updateUrlParams={false}
              rowId={(item) => `${item.Name}-${item["Date & Time"] ?? item.Name}`}
            />
          ) : (
            <div className="text-center py-12 text-gray-400">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <BeakerIcon className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium">No medications found</p>
            </div>
          )}
        </div>
      )}
    </BlurredCard>
  );
};

export default MedicationCard;
